import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from '@/components/layout/PageContainer';
import { Toast } from '@/components/common/Toast';
import {
  sendOTPForPasswordReset,
  verifyOTPForPasswordReset,
  resetPasswordWithOTP
} from '@/services/settings';
import { PasswordResetHeader } from '@/components/desktop/auth/PasswordResetHeader';
import { EmailInputStep } from '@/components/desktop/auth/EmailInputStep';
import { OTPVerificationStep } from '@/components/desktop/auth/OTPVerificationStep';
import { NewPasswordStep } from '@/components/desktop/auth/NewPasswordStep';

type Step = 'email' | 'verify' | 'reset';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('email');
  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [toast, setToast] = useState<{ show: boolean; message: string }>({ show: false, message: '' });

  const handleBack = () => {
    if (step === 'email') {
      navigate(-1);
    } else if (step === 'verify') {
      setStep('email');
    } else {
      setStep('verify');
    }
  };

  const handleSendCode = async () => {
    if (!email) {
      setToast({ show: true, message: '이메일을 입력해주세요' });
      return;
    }

    setIsLoading(true);
    try {
      const result = await sendOTPForPasswordReset(email);
      
      if (result.success) {
        setStep('verify');
        setToast({ show: true, message: '인증번호가 이메일로 발송되었습니다' });
      } else {
        setToast({ show: true, message: result.error || '인증번호 발송에 실패했습니다' });
      }
    } catch (error) {
      console.error('OTP send error:', error);
      setToast({ show: true, message: '인증번호 발송 중 오류가 발생했습니다' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setToast({ show: true, message: '6자리 인증번호를 입력해주세요' });
      return;
    }

    setIsLoading(true);
    try {
      const result = await verifyOTPForPasswordReset(email, verificationCode);
      
      if (result.success) {
        setStep('reset');
        setToast({ show: true, message: '인증이 완료되었습니다' });
      } else {
        setToast({ show: true, message: result.error || '인증번호가 올바르지 않습니다' });
      }
    } catch (error) {
      console.error('OTP verify error:', error);
      setToast({ show: true, message: '인증 확인 중 오류가 발생했습니다' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 8) {
      setToast({ show: true, message: '비밀번호는 8자리 이상이어야 합니다' });
      return;
    }

    if (newPassword !== confirmPassword) {
      setToast({ show: true, message: '비밀번호가 일치하지 않습니다' });
      return;
    }

    if (!verificationCode || verificationCode.length !== 6) {
      setToast({ show: true, message: '6자리 인증번호를 다시 입력해주세요' });
      return;
    }

    setIsLoading(true);
    try {
      const result = await resetPasswordWithOTP(email, newPassword, verificationCode);
      
      if (result.success) {
        setToast({ show: true, message: '비밀번호가 성공적으로 재설정되었습니다!' });
        
        // 2초 후 로그인 페이지로 이동
        setTimeout(() => {
          navigate('/login');
        }, 2000);
      } else {
        setToast({ show: true, message: result.error || '비밀번호 재설정에 실패했습니다' });
      }
    } catch (error) {
      console.error('Password reset error:', error);
      setToast({ show: true, message: '비밀번호 재설정 중 오류가 발생했습니다' });
    } finally {
      setIsLoading(false);
    }
  };

  const renderEmailStep = () => (
    <EmailInputStep
      email={email}
      isLoading={isLoading}
      onEmailChange={setEmail}
      onSubmit={handleSendCode}
    />
  );

  const renderVerifyStep = () => (
    <OTPVerificationStep
      email={email}
      verificationCode={verificationCode}
      isLoading={isLoading}
      onCodeChange={setVerificationCode}
      onSubmit={handleVerifyCode}
      onResendCode={() => {
        setVerificationCode('');
        handleSendCode();
      }}
    />
  );

  const renderResetStep = () => (
    <NewPasswordStep
      newPassword={newPassword}
      confirmPassword={confirmPassword}
      isLoading={isLoading}
      onNewPasswordChange={setNewPassword}
      onConfirmPasswordChange={setConfirmPassword}
      onSubmit={handleResetPassword}
    />
  );

  return (
    <div className="min-h-screen bg-white">
      <PageContainer>
        <div className="min-h-[80vh] flex items-center justify-center py-10">
          <div className="w-full max-w-lg bg-white rounded-2xl shadow-lg border border-gray-200 px-6 sm:px-10 py-10">
            <PasswordResetHeader currentStep={step} className="mb-6" />

            <div className="space-y-6">
              {step === 'email' && renderEmailStep()}
              {step === 'verify' && renderVerifyStep()}
              {step === 'reset' && renderResetStep()}
            </div>

            <div className="flex justify-center mt-8">
              <button
                onClick={handleBack}
                className="px-6 py-2 text-gray-600 hover:text-gray-800 text-[14px] transition-colors"
              >
                {step === 'email' ? '로그인으로 돌아가기' : '이전 단계로'}
              </button>
            </div>
          </div>
        </div>
      </PageContainer>

      {toast.show && (
        <Toast
          message={toast.message}
          isVisible={toast.show}
          onClose={() => setToast({ show: false, message: '' })}
        />
      )}
    </div>
  );
}
